import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import axios from "axios";

import oizomPolludrone from "../../../assets/oizomPolludrone.png";
import s1 from "../../../assets/s1.mp4";
import s2 from "../../../assets/s2.mp4";
import s3 from "../../../assets/s3.mp4";
import s4 from "../../../assets/s4.mp4";
import s5 from "../../../assets/s5.mp4";
import s6 from "../../../assets/s6.mp4";
import { GET_OIZOM_DATA } from "../../Api";

const PromotionSection = () => {
    const [oizomData, setOizomData] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const videoRef = useRef(null);
    const rotateRef = useRef(null);

const pad = (n) => n.toString().padStart(2, "0");

const now = new Date();
const formattedTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
  now.getDate()
)} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

    // Fetch Oizom data initially and every 5 minutes
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(GET_OIZOM_DATA, {
                    params: { timestamp: formattedTime },
                });
                setOizomData(response.data || []);
                setError(null);
            } catch (err) {
                setError("Failed to fetch data.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        const intervalId = setInterval(fetchData, 5 * 60 * 1000);
        return () => clearInterval(intervalId);
    }, []);

    // Rotate parameters every 5 seconds
    useEffect(() => {
        if (!oizomData.length) return;
        rotateRef.current = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % oizomData.length);
        }, 5000);
        return () => clearInterval(rotateRef.current);
    }, [oizomData]);

    // Update current time every second

    const { scrollYProgress } = useScroll();
    const scale = useTransform(scrollYProgress, [0, 1], [1, 0.5]);
    const borderRadius = useTransform(scrollYProgress, [0, 1], ["0px", "200px"]);

    const currentParam = oizomData[currentIndex] || {};
    const description = getDescription(currentParam?.out_param_name, currentParam?.out_value);

    function getDescription(name, value) {
        if (!name || value == null) return "";

        const val = parseFloat(value);
        const unit = currentParam?.out_unit_name ?? "";
        const lowerName = name.toLowerCase();

        if (lowerName.includes("temp")) {
            if (val >= 35) return "Scorching heat outside! Prolonged exposure may lead to dehydration or heatstroke. Stay indoors if possible, use sunscreen, and stay hydrated.";
            if (val >= 30) return "Hot weather—perfect for summer activities, but don’t forget to stay cool and avoid peak sunlight hours.";
            if (val >= 25) return "Mild and pleasant temperatures. Ideal for outings, workouts, or just soaking in some Vitamin D.";
            if (val >= 15) return "Cool and comfortable! A light jacket should keep you cozy if you're heading out.";
            return "Chilly temperatures today. Time for warm layers, and perhaps a hot cup of tea!";
        }

        if (lowerName.includes("humidity")) {
            if (val >= 80) return "Humidity is quite high—expect a muggy, sticky atmosphere. This can make the temperature feel warmer than it actually is.";
            if (val >= 60) return "Slightly humid. You might notice a bit of stickiness, especially indoors without proper ventilation.";
            if (val >= 30) return "Comfortably balanced humidity. Ideal indoor air conditions for both comfort and health.";
            return "Very dry air detected. This can lead to dry skin or irritation—consider using a humidifier or moisturizer.";
        }

        if (lowerName.includes("pm10") || lowerName.includes("pm2.5") || lowerName.includes("pm1") || lowerName.includes("pm100")) {
            if (val <= 50) return "Excellent air quality—clear skies and safe for all outdoor activities. Great day to go for a walk or run.";
            if (val <= 100) return "Moderate air quality—safe for most, but those with respiratory issues should monitor how they feel.";
            if (val <= 200) return "Unhealthy for sensitive groups—limit outdoor exposure if you have asthma or other lung conditions.";
            return "Very poor air quality. Avoid heavy exertion outside. Prolonged exposure can affect respiratory health.";
        }

        if (lowerName.includes("noise")) {
            if (val < 50) return "Quiet surroundings. Ideal for studying, working, or relaxing without disturbances.";
            if (val < 70) return "Moderate noise levels, like a busy street. Acceptable but might affect focus if prolonged.";
            return "High noise pollution—prolonged exposure may lead to stress or hearing fatigue. Consider using ear protection.";
        }

        if (lowerName.includes("light")) {
            if (val < 100) return "Very dim lighting—visibility is low, be cautious. Suitable for winding down or sleep settings.";
            if (val < 500) return "Soft lighting—pleasant for indoor reading or screen use. Gentle on the eyes.";
            if (val < 1000) return "Bright daylight—optimal for work or outdoor productivity. Natural light can boost mood!";
            return "Blazing brightness detected. Protect your eyes—avoid glare and consider using UV-filtered sunglasses.";
        }

        if (lowerName.includes("uv")) {
            if (val < 3) return "Low UV exposure. Safe to be outdoors for long durations. Minimal risk of sunburn.";
            if (val < 6) return "Moderate UV—sunscreen recommended if you're staying out for a while.";
            if (val < 8) return "High UV levels. Seek shade during peak sun hours. UV rays can damage skin even on cloudy days.";
            return "Extreme UV alert! Even a short time in the sun could cause burns. Stay in shade and use high-SPF protection.";
        }

        if (lowerName.includes("co2")) {
            if (val < 800) return "CO₂ levels are excellent—airflow and ventilation are adequate. Ideal for focus and alertness.";
            if (val < 1200) return "Moderately elevated CO₂. Consider improving airflow—can affect concentration in enclosed spaces.";
            return "High CO₂ concentration detected! May cause fatigue or drowsiness—open windows or use exhaust systems.";
        }

        if (lowerName.includes("co")) {
            if (val < 9) return "Carbon monoxide levels are within a safe range. No immediate risk detected.";
            if (val < 35) return "CO level slightly elevated—ensure adequate ventilation, especially if cooking indoors.";
            return "Hazardous CO levels! Immediately evacuate the area and ventilate—seek professional inspection.";
        }

        if (lowerName.includes("battery")) {
            if (val >= 75) return "Battery is fully charged and operational. You're good to go!";
            if (val >= 40) return "Battery level is moderate—monitor usage and consider recharging soon.";
            return "Low battery warning—system may power down or become unstable. Recharge or replace ASAP.";
        }

        return `Live reading: ${val} ${unit}. Data up-to-date and reflective of current environmental conditions.`;
    }



    // Dynamically build hourly temperatures
    const hourlyTemps = [];
    for (let i = 1; i <= 5; i++) {
        const timeKey = `avg_h${i}_time`;
        const valueKey = `avg_h${i}`;
        if (currentParam[timeKey] && currentParam[valueKey] != null) {
            hourlyTemps.push({
                time: currentParam[timeKey],
                value: `${Math.round(currentParam[valueKey])} ${currentParam.out_unit_name || ""}`,
            });
        }
    }


    return (
        <motion.div
            style={{ scale, borderRadius }}
            className="relative w-full overflow-hidden sm:w-[320px] md:w-[350px] lg:w-full flex-shrink-0 text-center h-[600px]"
        >
            {/* Background video or loader */}
            {loading ? (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50">
                    <div className="flex flex-col items-center space-y-4">
                        <svg className="h-16 w-16 animate-spin stroke-white" viewBox="0 0 256 256">
                            <line x1="128" y1="32" x2="128" y2="64" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24" />
                            <line x1="195.9" y1="60.1" x2="173.3" y2="82.7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24" />
                            <line x1="224" y1="128" x2="192" y2="128" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24" />
                            <line x1="195.9" y1="195.9" x2="173.3" y2="173.3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24" />
                            <line x1="128" y1="224" x2="128" y2="192" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24" />
                            <line x1="60.1" y1="195.9" x2="82.7" y2="173.3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24" />
                            <line x1="32" y1="128" x2="64" y2="128" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24" />
                            <line x1="60.1" y1="60.1" x2="82.7" y2="82.7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24" />
                        </svg>
                        <span className="text-lg text-white font-medium">
                            Fetching real-time data from Oizom Polludrone. Please wait...
                        </span>
                    </div>
                </div>
            ) : (
                <video
                    ref={videoRef}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="absolute top-0 left-0 w-full h-full object-cover"
                >
                    <source
                        src={
                            now.getHours() < 6
                                ? s6
                                : now.getHours() < 7
                                    ? s1
                                    : now.getHours() < 9
                                        ? s2
                                        : now.getHours() < 16
                                            ? s3
                                            : now.getHours() < 18
                                                ? s4
                                                : s5
                        }
                        type="video/mp4"
                    />
                    Your browser does not support the video tag.
                </video>
            )}

            {/* Overlay */}
            <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-70 z-10" />

            {/* Polludrone image */}
            <img
                src={oizomPolludrone}
                alt="Oizom Polludrone"
                className="hidden md:block absolute left-6 top-1/2 transform -translate-y-1/2 w-[450px] z-20"
            />

            {/* Content */}
            <div className="absolute left-0 md:left-[450px] top-0 md:top-1/2 md:transform md:-translate-y-1/2 text-white z-30 text-left flex flex-col justify-between h-full md:h-[80%] w-full md:w-[calc(100%-450px-2rem)] px-4 md:pr-8 pt-6 pb-6">
                {loading ? null : error ? (
                    <p className="text-2xl text-red-500">{error}</p>
                ) : (
                    <>
                        {/* Location */}
                        <div className="absolute md:static top-4 right-4 text-right text-gray-200">
                            <a
                                href="https://maps.app.goo.gl/W5VkcekTau7pTqrr5"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-300 underline font-medium inline-flex items-center gap-1"
                            >
                                <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 11c1.657 0 3-1.343 3-3S13.657 5 12 5s-3 1.343-3 3 1.343 3 3 3z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-4.5 8-13c0-4.418-3.582-8-8-8S4 4.582 4 9c0 8.5 8 13 8 13z" />
                                </svg>
                                Sun Teknolozy, Chennai
                            </a>
                            <p className="text-xs mt-1 text-gray-400">
                                {new Date().toLocaleDateString("en-US", {
                                    weekday: "long",
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                })}
                                ,{" "}
                                {new Date().toLocaleTimeString("en-US", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: false,
                                })}
                            </p>
                        </div>

                        {/* Parameter Reading + Description */}
                        <div className="mt-20 md:mt-0 px-2 flex flex-col space-y-3 sm:space-y-6">
  <p className="text-2xl md:text-2xl font-bold">{currentParam?.out_param_name ?? "--"}</p>

  {/* Value + Unit */}
  <div className="flex items-end gap-2">
    <span className="text-4xl md:text-6xl font-bold">
      {currentParam?.out_value != null ? parseFloat(currentParam.out_value).toFixed(1) : "--"}
    </span>
    <span className="text-lg md:text-2xl font-semibold text-gray-300">
      {currentParam?.out_unit_name ?? ""}
    </span>
  </div>

  {/* Spacer only on mobile */}
  <div className="block sm:hidden h-2" />

  {/* Low/High Stats */}
  <div className="flex gap-4 text-sm">
    <div className="flex items-center gap-2 text-cyan-400">
      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M19 9l-7 7-7-7" />
      </svg>
      <div>
        <div className="text-sm opacity-80">Lowest</div>
        <div className="text-sm font-semibold">{currentParam.lowest_value}</div>
      </div>
    </div>
    <div className="flex items-center gap-2 text-pink-300">
      <svg className="w-5 h-5 sm:w-6 sm:h-6 rotate-180" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M19 9l-7 7-7-7" />
      </svg>
      <div>
        <div className="text-sm opacity-80">Highest</div>
        <div className="text-sm font-semibold">{currentParam.highest_value}</div>
      </div>
    </div>
  </div>

  {/* Spacer only on mobile */}
  <div className="block sm:hidden h-2" />

  {/* Description */}
  <p className="text-sm sm:text-base md:text-md lg:text-md text-gray-300">{description}</p>
</div>


                        {/* Hourly Average */}
                        <div className="flex flex-col justify-end items-center mt-6">
                            <div className="flex justify-between w-full px-2 sm:px-4 md:pl-4 md:pr-24">
                                {hourlyTemps.map((entry, idx) => (
                                    <div key={idx} className="text-center min-w-[48px] sm:min-w-[60px]">
                                        <div className="text-xs sm:text-base md:text-lg font-medium">{entry.time}</div>
                                        <div className="text-sm sm:text-xl md:text-2xl font-bold">{entry.value.split(' ')[0]}</div>
                                        <div className="text-[10px] sm:text-sm md:text-base">{entry.value.split(' ')[1]}</div>
                                    </div>
                                ))}
                            </div>
                        </div>



                        {/* Navigation Buttons */}
                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                onClick={() => {
                                    clearInterval(rotateRef.current);
                                    setCurrentIndex((prev) => (prev - 1 + oizomData.length) % oizomData.length);
                                }}
                                className="w-8 h-8 md:w-14 md:h-14 rounded-full bg-white flex items-center justify-center shadow hover:bg-gray-300 transition"
                            >
                                <span className="text-sm md:text-2xl text-black">‹</span>
                            </button>
                            <button
                                onClick={() => {
                                    clearInterval(rotateRef.current);
                                    setCurrentIndex((prev) => (prev + 1) % oizomData.length);
                                }}
                                className="w-8 h-8 md:w-14 md:h-14 rounded-full bg-white flex items-center justify-center shadow hover:bg-gray-300 transition"
                            >
                                <span className="text-sm md:text-2xl text-black">›</span>
                            </button>
                        </div>
                    </>
                )}
            </div>
        </motion.div>
    );


};

export default PromotionSection;
